import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {filter, Subscription} from 'rxjs';
import {ClientStore} from '@/app/core/states/clientes-state/client-state.store';
import {MobileNavService} from '../mobile-header-component/service/mobile-nav.service';
import {UserRoleStore} from '@/app/core/states/user-role-stare/client-role-state.store';
import {toSignal} from '@angular/core/rxjs-interop';
import {CampaignStoreService} from '@/app/features/campaña/services/campaign-store.service';
import {withReportChildren} from '@/app/utils/report-menu.util';
import {AuthService} from '@/app/features/auth/services/auth.service';
import {ThemeService} from '@/app/core/services/theme/theme.service';

export interface NavItem {
  label: string;
  icon: string;
  route?: string;
  roles?: string[];
  submenuKey?: string;
  children?: {
    label: string;
    icon?: string;
    route: string;
  }[];
}

@Component({
  selector: 'app-nav-bar',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './nav-bar.html',
  styleUrl: './nav-bar.scss',
})
export class NavbarComponent implements OnInit, OnDestroy {
  private store = inject(ClientStore);
  private router = inject(Router);
  nav = inject(MobileNavService);
  private userState = inject(UserRoleStore);
  private authService = inject(AuthService);
  private themeService = inject(ThemeService);

  public brandName = this.themeService.brandName;

  public navBarLogo = computed(() => {
    const theme = this.themeService.getTheme();
    if (!theme?.images) return undefined;
    return this.isMobile() ? theme.images.mobileLogo : theme.images.companyLogo;
  });
  private mql = window.matchMedia('(max-width: 1024px)');
  isMobile = signal(this.mql.matches);

  activeSubmenu: string | null = null;

  private subs = new Subscription();

  private campaignStore = inject(CampaignStoreService);
  private selectedCampaignSig = toSignal(
    this.campaignStore.selectedCampaignClient$,
    { initialValue: null }
  );

  ngOnInit(): void {
    // sync estado inicial por URL
    this.setActiveSubmenuByRoute(this.router.url);

    // escuchar navegación para mantener abierto el submenú del padre activo
    this.subs.add(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe((e: NavigationEnd) =>
          this.setActiveSubmenuByRoute(e.urlAfterRedirects)
        )
    );

    const onMqChange = (ev: MediaQueryListEvent) =>
      this.isMobile.set(ev.matches);
    this.mql.addEventListener('change', onMqChange);
    this.subs.add({
      unsubscribe: () => this.mql.removeEventListener('change', onMqChange),
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Mostrar submenú solo si corresponde y (en CLIENTES) existe publicId */
  showSubmenu(item: NavItem): boolean {
    const isActive = item.submenuKey === this.activeSubmenu;
    const hasChildren = !!item.children?.length;

    if (item.submenuKey === 'CLIENTES') {
      const id = this.store.client()?.publicId ?? '';
      const hasClienteId = id.length > 0;
      return isActive && hasChildren && hasClienteId;
    }
    return isActive && hasChildren;
  }

  /** Toggle genérico por clave */
  toggleSubmenu(key?: string): void {
    if (!key) return;
    this.activeSubmenu = this.activeSubmenu === key ? null : key;
  }

  /** Determinar qué submenú abrir en base a la URL actual */
  setActiveSubmenuByRoute(url: string): void {
    const matchedItem = this.navItems().find(
      (item) =>
        (item.route && url.startsWith(item.route)) ||
        item.children?.some((child) => url.startsWith(child.route))
    );
    this.activeSubmenu = matchedItem?.children?.length
      ? matchedItem?.submenuKey ?? null
      : null;
  }

  /** Padre: en desktop también togglea el submenú; en móvil solo navega y cierra */
  onParentClick(item: NavItem): void {
    // Para USER, limpiar campaña al cambiar de sección
    const user = this.userState.user();
    if (user?.group === 'USER') {
      this.campaignStore.setSelectedCampaign(null);
    }

    if (item.children?.length && !this.isMobile()) {
      this.toggleSubmenu(item.submenuKey);
    }
    if (this.isMobile()) {
      this.nav.close();
    }
  }

  /** Caret: solo abre/cierra submenú (no navega). */
  onCaretClick(item: NavItem, ev: MouseEvent): void {
    ev.stopPropagation();
    ev.preventDefault();
    if (!item.children?.length) return;
    this.toggleSubmenu(item.submenuKey);
  }

  /** Hijo: navega y, si es móvil, cierra el drawer */
  onChildClick(): void {
    if (this.isMobile()) this.nav.close();
  }

  logout(): void {
    if (this.isMobile()) {
      setTimeout(() => { this.nav.close(); }, 100);
    }

    this.authService.logout();
  }

  readonly navItems = computed<NavItem[]>(() => {
    const theme = this.themeService.getTheme();
    const user = this.userState.user() as { group: string } | null;
    if (!user || !theme) return [];
    const total = this.selectedCampaignSig()?.totalEmployees ?? null;

    const baseItems: Omit<NavItem, 'icon'>[] = [
      {
        label: 'Inicio',
        route: '/inicio',
        roles: ['ADMIN'],
      },
      {
        label: 'Usuarios',
        route: '/usuarios',
        roles: ['ADMIN'],
        submenuKey: 'USUARIOS',
        children: [
          { label: 'Crear usuario', route: '/usuarios/crear' },
        ],
      },
      {
        label: 'Clientes',
        route: '/clientes',
        roles: ['ADMIN'],
        submenuKey: 'CLIENTES',
        children: [
          /* {
            label: 'Ocupación',
            icon: 'assets/icons/folder.svg',
            route: '/clientes/ocupacion',
          }, */
          {
            label: 'Departamento',
            icon: 'assets/icons/centros_trabajo.svg',
            route: '/clientes/departamento',
          },
          {
            label: 'Campañas',
            icon: 'assets/icons/resultados.svg',
            route: '/clientes/campañas',
          },
        ],
      },
      {
        label: 'Campaña',
        route: '/campaña',
        submenuKey: 'CAMPAÑA',
        roles: ['USER'],
      },
      {
        label: 'Resultados',
        route: '/resultados',
        submenuKey: 'RESULTADOS',
        roles: ['USER', 'ADMIN'],
      },
      {
        label: 'Reportes',
        route: '/reportes',
        submenuKey: 'REPORTES',
        roles: ['USER','ADMIN'],
        children: [
          { label: 'Reporte de Guía I', route: '/reportes/guia-1' },
          { label: 'Reporte de Guía II', route: '/reportes/guia-2' },
          { label: 'Reporte de Guía III', route: '/reportes/guia-3' },
        ],
      },
      {
        label: 'Ayuda',
        route: '/ayuda',
        submenuKey: 'AYUDA',
        roles: ['USER', 'ADMIN'],
        children: [
          { label: 'Política de privacidad', route: '/ayuda/politica' },
          { label: 'Contáctanos', route: '/ayuda/contactanos' },
        ],
      },
      {
        label: 'Cerrar sesión',
        roles: ['USER', 'ADMIN'],
      }
    ];

    const filteredItems = baseItems.filter((item) =>
      item.roles?.includes(user.group)
    );

    const itemsWithIcons = filteredItems.map((item) => {
      let iconPath = '';
      switch (item.label) {
        case 'Inicio':
          iconPath = theme.icons.homeIcon.path;
          break;
        case 'Usuarios':
          iconPath = theme.icons.usersIcon.path;
          break;
        case 'Clientes':
          iconPath = theme.icons.clientsIcon.path;
          break;
        case 'Campaña':
          iconPath = theme.icons.campaignsIcon.path;
          break;
        case 'Resultados':
          iconPath = theme.icons.resultsIcon.path;
          break;
        case 'Reportes':
          iconPath = theme.icons.reportsIcon.path;
          break;
        case 'Ayuda':
          iconPath = theme.icons.helpIcon.path;
          break;
        case 'Cerrar sesión':
          iconPath = theme.icons.logoutIcon.path;
          break;
        default:
          iconPath = '';
      }

      return {
        ...item,
        icon: iconPath,
      } as NavItem;
    });

    return withReportChildren(itemsWithIcons, total);
  });
}
